"use client";

import { useState, useRef, useEffect } from "react";
import { MapPin, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/siteData";

interface LazyMapProps {
  /** Optional extra classes on the outer rounded container. */
  className?: string;
  /** Aspect ratio utility class for the map frame. Defaults to 16/9. */
  aspectClassName?: string;
  /**
   * If true, the iframe only loads on click (most PSI-friendly).
   * If false (default), it also auto-loads when scrolled into view.
   */
  clickToLoadOnly?: boolean;
}

// Eagle Chimney Service GMB — FTID 0x89b7c3871aaeaaab:0xcb0deef056fff265.
// Pin shows business location; service area covers all of MD/DC.
const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3094.5!2d-76.9114937!3d39.0470955!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c3871aaeaaab:0xcb0deef056fff265!2sEagle+Chimney+Service!5e0!3m2!1sen!2sus!4v1";

export default function LazyMap({
  className = "",
  aspectClassName = "aspect-[16/9]",
  clickToLoadOnly = false,
}: LazyMapProps) {
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-load when scrolled into view (unless click-to-load only).
  useEffect(() => {
    if (clickToLoadOnly || loaded) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [clickToLoadOnly, loaded]);

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden rounded-2xl border border-slate-200 bg-white ${className}`}
    >
      <div className={`relative ${aspectClassName} bg-slate-100`}>
        {loaded ? (
          <iframe
            title={`${SITE.name} Location — ${SITE.addressDisplay}`}
            src={MAP_EMBED_SRC}
            width="100%"
            height="100%"
            style={{ border: 0, position: "absolute", inset: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-100 hover:bg-slate-200/70 transition-colors"
            
          >
            <div className="w-14 h-14 rounded-xl bg-[#E8BD4E]/10 text-[#7A5500] flex items-center justify-center group-hover:scale-110 transition-transform">
              <MapPin className="h-7 w-7" aria-hidden="true" />
            </div>
            <span className="text-sm font-semibold text-[#0A0A0A] text-center px-4">
              {SITE.addressDisplay}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#7A5500]">
              View map
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
      <div className="p-4 text-center">
        <p className="text-sm text-slate-700">
          <MapPin className="inline h-4 w-4 text-[#7A5500] mr-1" aria-hidden="true" />
          {SITE.addressDisplay} — Serving All of Maryland &amp; DC
        </p>
      </div>
    </div>
  );
}
